/**
 * Admin → Royalties (iter-34) — pay authors out by sweeping pending ledger rows.
 *
 * Lists every author with an unpaid balance + last-sale-at, lets the admin
 * click "Mark paid" to flush ALL their pending royalty rows into a single
 * `royalty_payouts` record. A history table below shows past payouts.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { CheckCircle2, Wallet, History, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { royaltiesAdminApi } from '@/lib/api';
import { PanelHeader } from './_shared';

const fmtUsd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
};

function MarkPaidButton({ author, onDone }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  // iter-53n++ pass 12c — mint an idempotency key per dialog session
  // so a retry inside the same confirm dialog reuses the same key
  // (dedupes on the backend). A new dialog session mints a new key.
  const [idemKey, setIdemKey] = useState('');
  const openKey = () => setIdemKey(
    (crypto.randomUUID ? crypto.randomUUID()
      : `mp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
  );
  const submit = async () => {
    setBusy(true);
    try {
      const r = await royaltiesAdminApi.markPaid(author.author_user_id, note, idemKey);
      toast.success(`Paid ${fmtUsd(r.total_cents)} to ${author.author_email || author.author_user_id} (${r.row_count} rows).`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark paid.');
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog onOpenChange={(o) => { if (o) openKey(); }}>
      <AlertDialogTrigger asChild>
        <Button size="sm" className="h-7 gap-1.5"
                disabled={author.pending_cents === 0}
                data-testid={`royalty-mark-paid-${author.author_user_id}`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Mark paid
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark {fmtUsd(author.pending_cents)} as paid?</AlertDialogTitle>
          <AlertDialogDescription>
            Sweeps every UNPAID royalty row for <b>{author.author_email || author.author_name || author.author_user_id}</b> ({author.pending_sales} sales) into a single payout record. This action records the payout in the audit log and cannot be undone in-app.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label className="text-[11px]">Note (optional — e.g. payout method / Stripe transfer id)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid={`royalty-payout-note-${author.author_user_id}`} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={submit} disabled={busy}
                              data-testid={`royalty-mark-paid-confirm-${author.author_user_id}`}>
            {busy ? 'Saving…' : `Confirm payout · ${fmtUsd(author.pending_cents)}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function RoyaltiesPanel() {
  const [pending, setPending] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [p, h] = await Promise.all([
        royaltiesAdminApi.pending(),
        royaltiesAdminApi.payouts(),
      ]);
      setPending(p.pending || []);
      setPayouts(h.payouts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load royalties.');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const totalPending = pending.reduce((acc, r) => acc + (r.pending_cents || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-royalties-panel">
      <PanelHeader
        title="Royalties & payouts"
        subtitle="One row per author with a pending balance. Click Mark paid to sweep their unpaid ledger rows into a single payout. The payout amount you pay externally (Stripe transfer / bank push) should match the amount shown here."
        onRefresh={load}
      />

      {/* Pending payouts */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-[13px] font-semibold">
              <Wallet className="h-4 w-4" /> Pending payouts
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Total pending across all authors: <b className="text-foreground font-mono-stat">{fmtUsd(totalPending)}</b>
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={load} className="gap-1.5" disabled={busy}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
        <div className="mt-3 overflow-hidden rounded-sm border border-border">
          <table className="w-full text-[12px]">
            <thead className="bg-panel-header/60 text-[11px] text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2">Author</th>
                <th className="px-3 py-2 text-right">Sales</th>
                <th className="px-3 py-2 text-right">Pending</th>
                <th className="px-3 py-2">First sale</th>
                <th className="px-3 py-2">Last sale</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No pending payouts.</td></tr>
              )}
              {pending.map((p) => (
                <tr key={p.author_user_id} className="border-t border-border" data-testid={`royalty-pending-row-${p.author_user_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{p.author_name || '—'}</div>
                    <div className="font-mono-stat text-[10.5px] text-muted-foreground">{p.author_email}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-stat">{p.pending_sales}</td>
                  <td className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{fmtUsd(p.pending_cents)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(p.first_sold_at)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(p.last_sold_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <MarkPaidButton author={p} onDone={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
          <History className="h-4 w-4" /> Payout history
        </div>
        <div className="mt-3 overflow-hidden rounded-sm border border-border">
          <table className="w-full text-[12px]">
            <thead className="bg-panel-header/60 text-[11px] text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2">Paid at</th>
                <th className="px-3 py-2">Author</th>
                <th className="px-3 py-2">Period</th>
                <th className="px-3 py-2 text-right">Rows</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">No payouts recorded yet.</td></tr>
              )}
              {payouts.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`royalty-payout-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono-stat">{fmtDate(p.paid_at)}</td>
                  <td className="px-3 py-2">
                    <div>{p.author_name || '—'}</div>
                    <div className="font-mono-stat text-[10.5px] text-muted-foreground">{p.author_email}</div>
                  </td>
                  <td className="px-3 py-2 font-mono-stat">{p.period_yyyymm}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">{p.row_count}</td>
                  <td className="px-3 py-2 text-right font-mono-stat font-semibold text-primary">{fmtUsd(p.total_cents)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{p.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
